import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { environment } from 'usermgmt';

export interface StationSurvey {
  lidar: boolean;
  ortho: boolean;
  ts: boolean;
}

export interface Station {
  id: number;
  name: string;
  code: string;
  section: string;
  zone: string;
  division: string;
  esp: 'approved' | 'digitised' | 'validated' | 'under_review' | 'pending_review' | null;
  sip: 'approved' | 'digitised' | 'validated' | 'under_review' | 'uploaded' | null;
  lop: 'approved' | 'digitised' | null;
  toc: boolean;
  survey: StationSurvey;
  versions: string | null;
  lastTime: string;
  lastBy: string;
  stationClass?: string;
  cll?: string;
  stationId?: string;
  stationTitle?: string;
  trainDirection?: string;
  adjacentUp?: string;
  adjacentDn?: string;
}

export interface DocumentSubmissionPayload {
  user_id: string;
  station_id: string;
  section_id: string;
  division_id: string;
  zone_id: string;
  document_type_code: string;
  file_name: string;
  expires_in: number;
}

export interface ExtractionPayload {
  zone_id: string;
  division_id: string;
  section_id: string;
  station_id: string;
  document_type: string;
  station_name: string;
  file_name: string;
}

@Injectable({ providedIn: 'root' })
export class StationService {
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly baseUrl = environment.urlMapping.PROJ_API_V1_URL;

  stationsSignal = signal<Station[]>([]);
  zonesSignal = signal<any[]>([]);
  divisionsSignal = signal<any[]>([]);
  sectionsSignal = signal<any[]>([]);
  activeAssetsData = signal<any>(null);
  activeDxfFile = signal<File | null>(null);

  activePolls = new Map<string, ReturnType<typeof setInterval>>();
  pollingCompleted = new Subject<{ submissionId: string; status: 'SUCCESS' | 'FAILED' }>();

  stationDocuments = signal<Record<string, Record<string, any[]>>>({
    BVRT: {
      esp: [
        {
          fileName: 'architectural_-_annotation_scaling_and_multileaders.dwg',
          version: '7.0',
          status: 'Approved',
          tone: 'success',
          user: 'Laxmi Prasad',
          initials: 'VP',
          role: 'Minister of Railways',
          uploaded: '1 day ago',
          approvalStatus: 'Approved'
        }
      ],
      sip: [],
      lop: [],
      survey: [
        {
          fileName: 'BVRT_LiDAR_Survey.las',
          version: '1.0',
          status: 'Completed',
          tone: 'success',
          user: 'Laxmi Prasad',
          initials: 'VP',
          role: 'SSE',
          uploaded: '2 days ago',
          approvalStatus: 'Approved'
        }
      ]
    },
    BZA: {
      esp: [
        {
          fileName: 'BZA_ESP_V1.dwg',
          version: '1.0',
          status: 'Approved',
          tone: 'success',
          user: 'Laxmi Prasad',
          initials: 'VP',
          role: 'SSE',
          uploaded: '1 day ago',
          approvalStatus: 'Approved'
        }
      ],
      sip: [],
      lop: [],
      survey: []
    }
  });

  private getRelativeTimeString(dateStr: string): string {
    if (!dateStr) return 'Just added';
    try {
      const cleanStr = dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + '+05:30';
      const past = new Date(cleanStr);
      const diffMs = Date.now() - past.getTime();
      if (isNaN(diffMs)) return 'Just added';

      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;

      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;

      return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Just added';
    }
  }

  private rewriteToRelativeUrl(url: string): string {
    try {
      if (!url.startsWith('http')) return url;
      const urlObj = new URL(url);
      const isInternal = urlObj.hostname.includes('ngrok')
        || urlObj.hostname.includes('localhost')
        || urlObj.hostname.includes('127.0.0.1')
        || urlObj.pathname.includes('/api/');
      if (!isInternal) return url;
      const relative = urlObj.pathname + urlObj.search;
      return relative.startsWith('/api') ? relative : '/api' + relative;
    } catch {
      return url;
    }
  }

  getStationsRaw(): Observable<Station[]> {
    return this.http.get<Station[]>(`${this.baseUrl}/stations`);
  }

  getStations(): Observable<Station[]> {
    return forkJoin({
      zones: this.getZones(),
      divisions: this.getDivisions(),
      sections: this.getSections(),
      stations: this.getStationsRaw()
    }).pipe(
      map((res: any) => {
        const rawStations = Array.isArray(res.stations) ? res.stations : (res.stations?.data || res.stations?.stations || []);
        const zones = res.zones || [];
        const divisions = res.divisions || [];
        const sections = res.sections || [];

        this.zonesSignal.set(zones);
        this.divisionsSignal.set(divisions);
        this.sectionsSignal.set(sections);

        const mapped = rawStations.map((station: any) => {
          const sectionObj = sections.find((sec: any) => sec.id === station.section_id);
          const divisionObj = sectionObj ? divisions.find((div: any) => div.id === sectionObj.division_id) : null;
          const zoneObj = divisionObj ? zones.find((z: any) => z.id === divisionObj.zone_id) : null;

          const docs = station.documents || [];
          const espDoc = docs.find((d: any) => d.document_type === 'ESP');
          const sipDoc = docs.find((d: any) => d.document_type === 'SIP');
          const lopDoc = docs.find((d: any) => d.document_type === 'LOP');

          return {
            ...station,
            zone: zoneObj?.code ?? 'SCR',
            division: divisionObj?.name ?? 'Vijayawada',
            section: sectionObj?.name ?? 'Nidadavolu-Bhimavaram',
            esp: espDoc ? (espDoc.count > 0 ? 'approved' : null) : (station.esp || null),
            sip: sipDoc ? (sipDoc.count > 0 ? 'approved' : null) : (station.sip || null),
            lop: lopDoc ? (lopDoc.count > 0 ? 'approved' : null) : (station.lop || null),
            toc: station.toc || false,
            survey: station.survey || { lidar: false, ortho: false, ts: false },
            versions: station.versions || null,
            lastActivityDate: station.last_updated_at || station.created_at || new Date().toISOString(),
            lastTime: this.getRelativeTimeString(station.last_updated_at || station.created_at),
            lastBy: (() => {
              const by = station.last_updated_by || station.created_by || 'Admin';
              return by.charAt(0).toUpperCase() + by.slice(1);
            })()
          };
        });
        this.stationsSignal.set(mapped);
        return mapped;
      })
    );
  }

  getClonedRows(code: string): Record<string, any[]> {
    return this.stationDocuments()[code.toUpperCase()] || { esp: [], sip: [], lop: [], survey: [] };
  }

  updateClonedRows(code: string, newRows: Record<string, any[]>): void {
    this.stationDocuments.update(prev => ({ ...prev, [code.toUpperCase()]: newRows }));
  }

  getStationByCode(code: string): Station | undefined {
    return this.stationsSignal().find(s => s.code.toLowerCase() === code.toLowerCase());
  }

  getZones(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/zones`);
  }

  getDivisions(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/divisions`);
  }

  getSections(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/sections`);
  }

  addStation(station: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/stations`, station);
  }

  updateStation(idOrCode: any, updates: Partial<Station>): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/stations/${idOrCode}`, updates);
  }

  deleteStation(idOrCode: any): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/stations/${idOrCode}`);
  }

  documentSubmission(payload: DocumentSubmissionPayload): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/submissions`, payload);
  }

  getSubmissions(stationId: string, userId: string = 'user_laxmiprasad_001'): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/submissions`, {
      params: { station_id: stationId, user_id: userId }
    });
  }

  getSubmissionStatus(submissionId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/submissions/${submissionId}`);
  }

  getSubmissionAssets(submissionId: string, userId: string = 'user_laxmiprasad_001', expiresIn: number = 3600): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/submissions/${submissionId}/assets`, {
      params: { user_id: userId, expires_in: expiresIn.toString() }
    });
  }

  getSubmissionVersions(submissionId: string, userId: string = 'user_laxmiprasad_001'): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/submissions/${submissionId}/versions`, {
      params: { user_id: userId }
    });
  }

  initiateVersionUpload(submissionId: string, userId: string = 'user_laxmiprasad_001', expiresIn: number = 3600): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/submissions/${submissionId}/versions`, {}, {
      params: { user_id: userId, expires_in: expiresIn.toString() }
    });
  }

  confirmSubmission(submissionId: string, userId: string = 'user_laxmiprasad_001'): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/submissions/${submissionId}/confirm`, {}, {
      params: { user_id: userId }
    });
  }

  confirmVersionUpload(submissionId: string, versionId: string, userId: string = 'user_laxmiprasad_001'): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/submissions/${submissionId}/versions/${versionId}/confirm`, {}, {
      params: { user_id: userId }
    });
  }

  deleteSubmission(submissionId: string, stationId: string, userId: string = 'user_laxmiprasad_001'): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/submissions/${submissionId}`, {
      params: { user_id: userId, station_id: stationId }
    });
  }

  documentStatusUpdate(submissionId: string, userId: string = 'user_laxmiprasad_001'): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/submissions/${submissionId}/assets/json?user_id=${userId}`, {});
  }

  triggerExtraction(submissionId: string, userId: string = 'user_laxmiprasad_001', payload: ExtractionPayload = {} as ExtractionPayload): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/submissions/${submissionId}/extract`, payload, {
      params: { user_id: userId }
    });
  }

  uploadToPresignedUrl(url: string, file: File): Observable<any> {
    return this.http.put(url, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' }
    });
  }

  updateAssetsJson(url: string, json: JSON): Observable<any> {
    return this.http.put(url, json, { headers: { 'Content-Type': 'application/json' } });
  }

  fetchJsonFromUrl(url: string): Observable<any> {
    return this.http.get(this.rewriteToRelativeUrl(url), { responseType: 'text' }).pipe(
      map((text: string) => {
        const keysToQuote = [
          'location_km',
          'location_chainage',
          'location_fcsb',
          'start_location_km',
          'start_location_chainage',
          'start_location_fcsb',
          'end_location_km',
          'end_location_chainage',
          'end_location_fcsb',
          'fcsb',
          'fc_value',
          'chainage',
          'distance_km',
          'distance',
          'distance_details_distance',
          'junction_location_km',
          'junction_location_chainage',
          'centerline_location_km',
          'center_line_location_chainage',
          'km'
        ];
        const regex = new RegExp(`"(${keysToQuote.join('|')})"\\s*:\\s*(-?\\d+\\.\\d+)\\b`, 'g');
        const processedText = text.replace(regex, '"$1": "$2"');
        return JSON.parse(processedText);
      })
    );
  }

  fetchFileFromUrl(url: string): Observable<Blob> {
    return this.http.get(this.rewriteToRelativeUrl(url), { responseType: 'blob' });
  }

  pollExtractionStatus(submissionId: string, docType: string, stationCode: string, stationId: any, fileName: string): void {
    if (docType.toLowerCase() !== 'esp' || this.activePolls.has(submissionId)) return;

    const intervalId = setInterval(() => {
      this.getSubmissionStatus(submissionId).subscribe({
        next: (res: any) => {
          const status = (res.digitize_status || res.status_code || res.status || '').toUpperCase();

          if (['EXTRACTED', 'DIGITIZED', 'GENERATED', 'SYSTEM_GENERATED'].includes(status)) {
            this.stopPoll(submissionId);
            this.updateDocumentRowStatus(stationCode, docType, submissionId, status, res.approval_status || 'Approved', 'success');
            this.updateStation(stationId, { [docType]: 'generated' } as any).subscribe({
              next: () => this.getStations().subscribe({
                next: (stations: any) => {
                  const data = Array.isArray(stations) ? stations : (stations?.data || stations?.stations || []);
                  this.stationsSignal.set(data);
                  this.pollingCompleted.next({ submissionId, status: 'SUCCESS' });
                }
              })
            });
            this.messageService.add({
              severity: 'success',
              summary: 'Extraction Complete',
              detail: `Extraction complete for ${fileName}. Please review the results before proceeding.`
            });
          } else if (['FAILED', 'EXTRACTION_FAILED', 'EXTRACTION FAILED'].includes(status)) {
            this.stopPoll(submissionId);
            this.updateDocumentRowStatus(stationCode, docType, submissionId, 'FAILED', 'Rejected', 'danger');
            this.pollingCompleted.next({ submissionId, status: 'FAILED' });
            this.messageService.add({ severity: 'error', summary: 'Extraction Failed', detail: `Extraction failed for ${fileName}.` });
          }
        },
        error: (err: any) => {
          if (err.status === 404) this.stopPoll(submissionId);
        }
      });
    }, 5000);

    this.activePolls.set(submissionId, intervalId);
  }

  stopAllPolling(): void {
    this.activePolls.forEach(id => clearInterval(id));
    this.activePolls.clear();
  }

  private stopPoll(submissionId: string): void {
    clearInterval(this.activePolls.get(submissionId));
    this.activePolls.delete(submissionId);
  }

  private updateDocumentRowStatus(stationCode: string, docType: string, submissionId: string, status: string, approvalStatus: string, tone: string): void {
    const currentRows = this.stationDocuments()[stationCode.toUpperCase()] || { esp: [], sip: [], lop: [], survey: [] };
    const updatedList = (currentRows[docType] || []).map((doc: any) =>
      doc.submissionId === submissionId ? { ...doc, status, tone, approvalStatus } : doc
    );
    this.updateClonedRows(stationCode, { ...currentRows, [docType]: updatedList });
  }
}
